import type { PreparedAuctionItem } from '@autobid/ui/types/components/AuctionsData'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { usePdfStore } from '@autobid/nuxt-pinia-store/store/usePdfStore'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { invariant } from '@autobid/ui/utils/invariant'

interface HandleCatalogProps {
  type: 'xls' | 'pdf'
  auction: Pick<PreparedAuctionItem, 'catalog' | 'restrictions' | 'appId' | 'stage' | 'type'>
  mode: 'open' | 'download'
}

export function useCatalog() {
  const { t, locale } = useI18n()
  const { sessionData, isAuthed } = useAutobidAuth()
  const { showPdfDialog } = usePdfStore()

  async function handleCatalog(props: HandleCatalogProps) {
    const pushPromise = push.promise({
      title: t('auction-catalog-popover.preparing-catalog'),
      message: t('auction-catalog-popover.preparing-catalog-message'),
    })

    try {
      const resp = await fetchCatalog(props)

      if (props.mode === 'download') {
        const fileResponse = await fetch(resp.data.url, {
          headers: sessionData.value?.accessToken ? { Authorization: sessionData.value.accessToken } : undefined,
        })
        const contentDisposition = fileResponse.headers.get('content-disposition')
        const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] ?? ''
        if (!fileResponse.ok) {
          throw new Error(`File fetch failed: ${fileResponse.status}`)
        }

        const blob = await fileResponse.blob()

        const href = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = href
        link.download = filename
        link.click()
        URL.revokeObjectURL(link.href)
      }
      else {
        invariant(
          props.auction.type === 'LIVE',
          'Viewing the catalog is not available for this type of auction',
        )
        // LIVE auctions do not require auhtorization to view the catalog
        // For other auction types we need to implement the authorization flow in `view` endpoint
        const url = new URL(resp.data.view)
        url.searchParams.set('auth', isAuthed.value ? '1' : '0')

        const pdfUrl = url.toString()

        showPdfDialog(`pdf-${pdfUrl}`, pdfUrl)
      }

      pushPromise.resolve(t('auction-catalog-popover.preparing-catalog-success'))
    }
    catch (e) {
      /**
       * `try/catch` to handle the error message with confidence.
       * 1. Attempt to access the error from the catalog service response
       * 2. Fallback to default `getError` pattern that we use
       */
      function displayError() {
        try {
          return (e as { data: { error: { message: string } } }).data.error.message
        }
        catch {
          return getError(e)?.message ?? t('unexpected-error')
        }
      }
      pushPromise.reject({
        title: t('auction-catalog-popover.preparing-catalog-failed'),
        message: displayError(),
      })
    }
  }

  function fetchCatalog({ type, auction }: HandleCatalogProps) {
    async function prepareCatalog(attempt = 0) {
      interface InitResponse {
        status: 'INITIALIZED'
      }
      interface InQueueResponse {
        status: 'IN_QUEUE'
      }
      interface ReadyResponse {
        status: 'READY'
        data: {
          url: string
          view: string
        }
      }
      const URL_MAP = {
        xls: auction.catalog?.urlXLS,
        pdf: auction.catalog?.url,
      } as const
      invariant(URL_MAP[type])
      const currentUrl = new URL(URL_MAP[type])
      currentUrl.searchParams.set('lang', locale.value)
      const response = await $fetch<
        InitResponse | InQueueResponse | ReadyResponse
      >(currentUrl.toString(), {
        method: 'post',
        headers: sessionData.value?.accessToken ? { Authorization: sessionData.value.accessToken } : undefined,
      })

      if (response?.status !== 'READY' && attempt < 3) {
        // wait 10s until generation completes
        await new Promise(resolve => setTimeout(resolve, 10000))
        return prepareCatalog(attempt + 1)
      }
      else if (response?.status !== 'READY') {
        throw response
      }

      return response
    }

    return prepareCatalog()
  }

  return { handleCatalog }
}
